import React, { useState } from 'react';
import { Upload, CheckCircle2, Loader2, Phone, QrCode } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { API } from './joinConfig';

export const QR_CODE_URL = "https://customer-assets-4nw71qhi.emergentagent.net/job_87e159dc-3189-4727-ae1e-bf728f7f511e/artifacts/o0l6x9za_image.png";

export const PaymentPanel = ({ memberNumber, mobile }) => {
  const { t } = useLanguage();
  const [state, setState] = useState('idle');

  const upload = async (file) => {
    if (!file) return;
    setState('busy');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const up = await fetch(`${API}/upload`, { method: 'POST', body: fd });
      if (!up.ok) throw new Error();
      const { url } = await up.json();
      const res = await fetch(`${API}/${memberNumber}/payment`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ mobile, payment_screenshot_url: url }) });
      setState(res.ok ? 'done' : 'error');
    } catch {
      setState('error');
    }
  };

  return (
    <div className="mt-6 rounded-2xl border border-[#FFD60A]/30 bg-[#FFD60A]/[0.05] p-4 sm:p-5" data-testid="join-payment-panel">
      <p className="font-join flex items-center gap-2 font-bold text-gray-900"><QrCode className="h-5 w-5 text-amber-600" /> {t({ en: 'Unlock full benefits — 100 BDT', bn: 'সম্পূর্ণ সুবিধা চালু করুন — ১০০ টাকা' })}</p>
      <p className="mt-1 text-xs text-gray-600">{t({ en: 'Pay once via bKash / Nagad / Rocket / card. After admin verifies, your card turns VERIFIED and partner discount codes unlock.', bn: 'bKash / নগদ / রকেট / কার্ডে একবার পরিশোধ করুন। অ্যাডমিন যাচাই করলে কার্ড যাচাইকৃত হবে ও পার্টনার ছাড় কোড চালু হবে।' })}</p>
      <div className="mt-4 flex flex-col items-center gap-4 sm:flex-row sm:items-start">
        <img src={QR_CODE_URL} alt="NDP payment QR" className="w-40 shrink-0 rounded-xl bg-white p-1" data-testid="join-payment-qr" />
        <div className="w-full space-y-3 text-sm">
          <p className="flex items-center gap-2 text-gray-900"><Phone className="h-4 w-4 text-party-green" /> <b className="font-join tracking-wider" data-testid="join-payment-number">01732341999</b> <span className="text-gray-500">bKash / Nagad</span></p>
          <p className="text-xs text-gray-500">NATIONAL DEMOCRATIC PARTY NDP · {t({ en: 'Reference', bn: 'রেফারেন্স' })}: <b className="text-gray-900">{memberNumber}</b></p>
          {state === 'done' ? (
            <p className="flex items-center gap-2 rounded-xl bg-green-50 p-3 font-semibold text-party-green" data-testid="join-payment-success"><CheckCircle2 className="h-5 w-5" /> {t({ en: 'Payment proof received. We will verify soon.', bn: 'পেমেন্ট প্রমাণ পাওয়া গেছে। শীঘ্রই যাচাই করা হবে।' })}</p>
          ) : (
            <label className={`flex cursor-pointer items-center justify-center gap-2 rounded-full border border-dashed border-gray-300 px-4 py-3 font-semibold text-gray-800 transition-colors duration-200 hover:bg-gray-100 ${state === 'busy' ? 'pointer-events-none opacity-60' : ''}`} data-testid="join-payment-upload-label">
              {state === 'busy' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {t({ en: 'Paid? Upload screenshot (optional)', bn: 'পরিশোধ করেছেন? স্ক্রিনশট দিন (ঐচ্ছিক)' })}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="join-payment-upload-input" />
            </label>
          )}
          {state === 'error' && <p className="text-xs text-party-red" data-testid="join-payment-error">{t({ en: 'Upload failed. Please try again.', bn: 'আপলোড ব্যর্থ। আবার চেষ্টা করুন।' })}</p>}
        </div>
      </div>
    </div>
  );
};

export const ScanPayCard = () => {
  const { t } = useLanguage();
  return (
    <div className="mt-5 flex items-center gap-4 rounded-[2rem] border border-party-green/30 bg-white p-4 shadow-lg sm:p-5" data-testid="join-scan-pay-card">
      <img src={QR_CODE_URL} alt="NDP payment QR" className="w-28 shrink-0 rounded-xl border sm:w-32" data-testid="join-scan-pay-qr" />
      <div className="min-w-0 text-sm">
        <p className="font-join flex items-center gap-2 text-base font-bold text-party-red"><QrCode className="h-5 w-5" /> {t({ en: 'Scan & Pay 100 BDT', bn: 'স্ক্যান করে ১০০ টাকা দিন' })}</p>
        <p className="mt-1 text-gray-600">{t({ en: 'One-time membership fee. Register first, then upload your payment screenshot to get verified faster.', bn: 'এককালীন সদস্য ফি। আগে নিবন্ধন করুন, তারপর দ্রুত যাচাইয়ের জন্য পেমেন্টের স্ক্রিনশট দিন।' })}</p>
        <p className="mt-2 flex items-center gap-1.5 font-bold text-gray-900"><Phone className="h-4 w-4 text-party-green" /> 01732341999 <span className="font-normal text-gray-500">bKash / Nagad</span></p>
        <div className="mt-2 flex flex-wrap gap-1">
          {['bKash', 'Nagad', 'Rocket', 'VISA', 'Mastercard'].map((m) => <span key={m} className="rounded border bg-gray-50 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500">{m}</span>)}
        </div>
      </div>
    </div>
  );
};
